import { ArrowLeft, MapPin, Home, Users, Star, Award, Shield, Phone, Mail, ChevronRight } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const AboutPage = () => {
  const teamMembers = [
    {
      name: "Sarah Johnson",
      title: "Founder & CEO",
      description: "Leading visionary with 15+ years in luxury real estate",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop"
    },
    {
      name: "Michael Kimani",
      title: "Head of Property Curation",
      description: "Expert in Nairobi's upmarket neighborhoods and investment properties",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop"
    },
    {
      name: "Grace Wanjiru",
      title: "Client Relations Director",
      description: "Dedicated to providing exceptional client experiences",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop"
    },
    {
      name: "David Mutua",
      title: "Market Analyst",
      description: "Specialist in property valuation and market trends",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop"
    }
  ];

  const values = [
    {
      icon: Shield,
      title: "Trust & Integrity",
      description: "Building lasting relationships through transparency and ethical practices in every transaction."
    },
    {
      icon: Award,
      title: "Excellence",
      description: "Delivering exceptional service that exceeds client expectations and sets industry standards."
    },
    {
      icon: Home,
      title: "Lifestyle Focus",
      description: "Understanding that you're not just buying property, you're investing in a way of life."
    },
    {
      icon: Users,
      title: "Personalized Service",
      description: "Tailored approaches that recognize each client's unique needs and aspirations."
    },
    {
      icon: Star,
      title: "Quality Curation",
      description: "Every property is personally vetted for excellence, value, and lifestyle compatibility."
    },
    {
      icon: MapPin,
      title: "Local Expertise",
      description: "Deep-rooted knowledge of Nairobi's neighborhoods and their unique characteristics."
    }
  ];

  return (
    <div className="bg-brand-canvas min-h-screen">
      <Header />
      <main className="pt-36 md:pt-40 w-full">
        {/* Hero Section */}
        <div className="bg-brand-gradient py-20 md:py-32">
          <div className="px-4 md:px-16 max-w-7xl mx-auto text-center">
            <div className="mb-6">
              <a href="/" className="inline-flex items-center gap-2 text-accent hover:text-accent/80 transition-colors">
                <ArrowLeft className="w-4 h-4" />
                <span className="font-body text-sm">Back to Home</span>
              </a>
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-light text-primary-foreground mb-8">
              About Wild Gates
            </h1>
            <div className="max-w-4xl mx-auto">
              <p className="font-body text-lg md:text-xl leading-relaxed text-primary-foreground/82">
                Our name is our philosophy. In a city as dynamic and vibrant as ours, finding a sanctuary that offers both luxury and security is paramount. 
                We specialize in curating living experiences within Nairobi's most coveted upmarket envelopes—Westlands, Riverside, Kileleshwa, Lavington, and Kilimani.
                We understand that our clients are not just looking for a property; they are looking for a lifestyle. 
                Whether it's the sleek, modern convenience of a furnished rental in Kilimani, the solid investment potential of an off-plan project in Westlands, or the family-centric serenity of a home in Riverside, Wild Gates is your trusted guide.
                Our team comprises hyper-local experts who don't just know the streets but understand the nuances of each neighborhood. We combine global standards of real estate professionalism with deep-rooted Kenyan hospitality. 
                We are gatekeepers of quality, curators of communities, and your partners in finding the perfect address behind the gate.
              </p>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="bg-brand-surface px-4 md:px-16 py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { number: "3+", label: "Years in Kenya", color: "from-[#E6D7AE42] to-[#F8F1DE]" },
              { number: "25+", label: "Properties Transferred", color: "from-[#FAF3E2] to-[#C5AE7826]" },
              { number: "5", label: "Prime Locations", color: "from-[#1A3F221A] to-[#CFBA8626]" },
              { number: "100%", label: "Client Satisfaction", color: "from-[#D8B7C13B] to-[#F8F1DE]" }
            ].map((stat, index) => (
              <div key={stat.label} className={`bg-gradient-to-br ${stat.color} border border-border/20 rounded-xl p-6 text-center backdrop-blur-sm`}>
                <p className="font-display text-3xl md:text-4xl font-light text-accent mb-2">
                  {stat.number}
                </p>
                <p className="font-body text-xs tracking-widest uppercase text-primary-foreground/80">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Our Values */}
        <div className="px-4 md:px-16 py-16">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-light text-primary-foreground mb-4">
              Our Core Values
            </h2>
            <p className="font-body text-base text-primary-foreground/75 max-w-2xl mx-auto">
              The principles that guide everything we do at Wild Gates
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((value, index) => (
              <div key={value.title} className="rounded-xl border border-primary-foreground/10 bg-background/10 p-6 backdrop-blur-sm hover:bg-background/15 transition-colors">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-2 bg-accent/20 rounded-lg">
                    <value.icon className="w-6 h-6 text-accent" />
                  </div>
                  <h3 className="font-display text-lg font-light text-primary-foreground">
                    {value.title}
                  </h3>
                </div>
                <p className="font-body text-sm text-primary-foreground/75 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Our Approach */}
        <div className="bg-brand-surface px-4 md:px-16 py-16">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-light text-primary-foreground mb-4">
              Our Approach
            </h2>
            <p className="font-body text-base text-primary-foreground/75 max-w-2xl mx-auto">
              How we deliver exceptional real estate experiences
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-background border border-border/20 rounded-xl p-6 hover:bg-accent/5 transition-colors">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-accent/10 rounded-lg">
                  <MapPin className="w-5 h-5 text-accent" />
                </div>
                <h3 className="font-display text-lg font-light text-foreground">
                  Hyper-Local Expertise
                </h3>
              </div>
              <p className="font-body text-sm text-muted-foreground leading-relaxed">
                Our team doesn't just know the streets—they understand the nuances, culture, and unique characteristics of each Nairobi neighborhood.
              </p>
            </div>
            
            <div className="bg-background border border-border/20 rounded-xl p-6 hover:bg-accent/5 transition-colors">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-accent/10 rounded-lg">
                  <Star className="w-5 h-5 text-accent" />
                </div>
                <h3 className="font-display text-lg font-light text-foreground">
                  Global Standards
                </h3>
              </div>
              <p className="font-body text-sm text-muted-foreground leading-relaxed">
                We combine international best practices in real estate with authentic Kenyan hospitality and service excellence.
              </p>
            </div>
            
            <div className="bg-background border border-border/20 rounded-xl p-6 hover:bg-accent/5 transition-colors">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-accent/10 rounded-lg">
                  <Home className="w-5 h-5 text-accent" />
                </div>
                <h3 className="font-display text-lg font-light text-foreground">
                  Lifestyle Curation
                </h3>
              </div>
              <p className="font-body text-sm text-muted-foreground leading-relaxed">
                We go beyond property features to understand how each home fits into your desired lifestyle and aspirations.
              </p>
            </div>
          </div>
        </div>

        {/* Team */}
        <div className="bg-brand-surface px-4 md:px-16 py-16">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-light text-primary-foreground mb-4">
              Meet Our Team
            </h2>
            <p className="font-body text-base text-primary-foreground/75 max-w-2xl mx-auto">
              The experts behind Wild Gates' success
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <div key={member.name} className="bg-card/95 border border-border/20 rounded-xl p-6 text-center hover:bg-card transition-colors">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
                />
                <h3 className="font-display text-lg font-light text-foreground mb-2">
                  {member.name}
                </h3>
                <p className="font-body text-sm text-accent mb-3">
                  {member.title}
                </p>
                <p className="font-body text-xs text-muted-foreground leading-relaxed">
                  {member.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact CTA */}
        <div className="bg-brand-surface px-4 md:px-16 py-16">
          <div className="text-center">
            <h2 className="font-display text-3xl md:text-4xl font-light text-primary-foreground mb-6">
              Ready to Find Your Perfect Address?
            </h2>
            <p className="font-body text-base text-primary-foreground/75 max-w-2xl mx-auto mb-8">
              Let our team of experts guide you to your ideal property in Nairobi's most prestigious neighborhoods
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#contact"
                className="inline-flex items-center gap-2 bg-accent text-accent-foreground font-body text-sm tracking-wider uppercase py-3 px-8 rounded-lg hover:bg-accent/90 transition-colors"
              >
                <Phone className="w-4 h-4" />
                Contact Us
              </a>
              <a
                href="/properties-for-sale"
                className="inline-flex items-center gap-2 border border-primary-foreground/20 text-primary-foreground font-body text-sm tracking-wider uppercase py-3 px-8 rounded-lg hover:bg-primary-foreground/10 transition-colors"
              >
                <Home className="w-4 h-4" />
                Browse Properties
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AboutPage;
